<template>
  <div>
    <NavigationBar
      showBackBtn
      titel="Dokumentvorlage erstellen"
    ></NavigationBar>
    <section>
      <template>
        <v-container fluid>
          <v-row class="match-height">
            <v-col cols="12">
              <v-stepper v-model="stepperState" vertical>
                <!-- Step 1: Basic Information -->
                <v-stepper-step :complete="stepperState > 1" step="1">
                  Grundeinstellungen
                </v-stepper-step>
                <v-stepper-content step="1">
                  <v-card outlined>
                    <v-card-text>
                      <DocumentTemplateEditor
                        v-model="template"
                        :organizationId="$route.params.organizationId"
                        @valid="isFormValid = $event"
                      />
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        depressed
                        color="primary"
                        @click="stepperState = 2"
                        :disabled="!isFormValid"
                      >
                        Weiter
                        <v-icon right>mdi-chevron-right</v-icon>
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>

                <!-- Step 2: File Upload -->
                <v-stepper-step :complete="stepperState > 2" step="2">
                  Word-Vorlage hochladen
                </v-stepper-step>
                <v-stepper-content step="2">
                  <v-card outlined>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12">
                          <v-alert type="info" text dense class="mb-4">
                            Laden Sie eine .docx-Datei hoch, in der Platzhalter
                            wie <code v-pre>&#123;Vorname&#125;</code>,
                            <code v-pre>&#123;Nachname&#125;</code> etc.
                            verwendet werden. Für mehrere Datensätze verwenden
                            Sie Schleifen:
                            <code v-pre
                              >&#123;#Members&#125;...&#123;/Members&#125;</code
                            >.
                          </v-alert>
                        </v-col>
                        <v-col cols="12">
                          <FileUploadDropzone
                            v-model="templateFiles"
                            :acceptedFileTypes="[
                              '.docx',
                              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                            ]"
                          />
                        </v-col>
                        <v-col cols="12" v-if="uploadProgress > 0">
                          <v-progress-linear
                            :value="uploadProgress"
                            color="primary"
                            height="8"
                            rounded
                          ></v-progress-linear>
                        </v-col>
                        <v-col cols="12" v-if="parsingPlaceholders">
                          <v-progress-linear
                            indeterminate
                            color="primary"
                          ></v-progress-linear>
                          <div class="text-caption text--secondary mt-1">
                            Platzhalter werden analysiert...
                          </div>
                        </v-col>
                        <v-col
                          cols="12"
                          v-if="
                            detectedPlaceholders &&
                            !parsingPlaceholders &&
                            templateFile
                          "
                        >
                          <v-card outlined>
                            <v-card-title class="text-subtitle-2">
                              <v-icon left small color="success"
                                >mdi-check-circle</v-icon
                              >
                              Platzhalter-Zuordnung
                            </v-card-title>
                            <v-card-text>
                              <PlaceholderMappingEditor
                                v-model="placeholderMappings"
                                :parsedPlaceholders="detectedPlaceholders"
                                :placeholderContext="
                                  template.placeholderContext
                                "
                              />
                            </v-card-text>
                          </v-card>
                        </v-col>
                        <v-col cols="12">
                          <v-expansion-panels flat>
                            <v-expansion-panel>
                              <v-expansion-panel-header>
                                <v-icon left class="mr-2"
                                  >mdi-tag-multiple</v-icon
                                >
                                Verfügbare Platzhalter anzeigen
                              </v-expansion-panel-header>
                              <v-expansion-panel-content>
                                <v-simple-table dense>
                                  <template v-slot:default>
                                    <thead>
                                      <tr>
                                        <th>Platzhalter</th>
                                        <th>Bezeichnung</th>
                                        <th>Beschreibung</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr
                                        v-for="placeholder in availablePlaceholders"
                                        :key="placeholder.tag"
                                      >
                                        <td>
                                          <code v-pre
                                            >&#123;{{
                                              placeholder.tag
                                            }}&#125;</code
                                          >
                                        </td>
                                        <td>{{ placeholder.label }}</td>
                                        <td>{{ placeholder.description }}</td>
                                      </tr>
                                    </tbody>
                                  </template>
                                </v-simple-table>
                                <v-alert
                                  type="info"
                                  text
                                  dense
                                  class="mt-3"
                                  v-if="loopTag"
                                >
                                  Für mehrere Datensätze verwenden Sie die
                                  Schleife:
                                  <code v-pre
                                    >&#123;#{{ loopTag }}&#125; ... &#123;/{{
                                      loopTag
                                    }}&#125;</code
                                  >
                                </v-alert>
                              </v-expansion-panel-content>
                            </v-expansion-panel>
                          </v-expansion-panels>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <v-btn text @click="stepperState = 1">
                        <v-icon left>mdi-chevron-left</v-icon>
                        Zurück
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn
                        depressed
                        color="success"
                        @click="saveTemplate"
                        :loading="loading"
                        :disabled="!templateFile"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Speichern
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>
              </v-stepper>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </section>
  </div>
</template>

<script>
import NavigationBar from "@/components/_systemwide/NavigationBar.vue";
import DocumentTemplateEditor from "@/components/_systemwide/DocumentTemplateEditor.vue";
import FileUploadDropzone from "@/components/_systemwide/FileUploadDropzone.vue";
import PlaceholderMappingEditor from "@/components/_systemwide/PlaceholderMappingEditor.vue";
import { storage, TaskEvent } from "@/firebase.js";
import { MY_ORGANIZATION } from "@/store/modules.js";
import { CREATE_DOCUMENT_TEMPLATE } from "@/store/action-types.js";
import {
  PLACEHOLDER_CONTEXTS,
  parsePlaceholders,
  automapPlaceholders,
  getEffectiveContexts,
} from "@/util/documentTemplateService.js";

export default {
  name: "document-template-new",
  components: {
    NavigationBar,
    DocumentTemplateEditor,
    FileUploadDropzone,
    PlaceholderMappingEditor,
  },
  data() {
    return {
      stepperState: 1,
      loading: false,
      isFormValid: false,
      templateFiles: [],
      uploadProgress: 0,
      detectedPlaceholders: null,
      placeholderMappings: {},
      parsingPlaceholders: false,
      template: {
        name: "",
        description: "",
        category: null,
        placeholderContext: "member",
        usageContexts: [],
        usagePolicy: "noRestriction",
        dataMode: "single",
      },
    };
  },
  computed: {
    templateFile() {
      return this.templateFiles.length > 0 ? this.templateFiles[0] : null;
    },
    effectiveContexts() {
      return getEffectiveContexts(this.template.placeholderContext);
    },
    availablePlaceholders() {
      const placeholders = [];
      this.effectiveContexts.forEach((ctx) => {
        const config = PLACEHOLDER_CONTEXTS[ctx];
        if (config) placeholders.push(...config.placeholders);
      });
      return placeholders;
    },
    loopTag() {
      const ctx = PLACEHOLDER_CONTEXTS[this.template.placeholderContext];
      return ctx ? ctx.loopTag : null;
    },
  },
  watch: {
    "template.placeholderContext"() {
      // Re-parse if file already selected
      if (this.templateFile) {
        this.parseTemplateFile();
      }
    },
    templateFile(newVal) {
      if (newVal) {
        this.parseTemplateFile();
      } else {
        this.detectedPlaceholders = null;
      }
    },
  },
  methods: {
    async parseTemplateFile() {
      if (!this.templateFile) return;
      this.parsingPlaceholders = true;
      try {
        this.detectedPlaceholders = await parsePlaceholders(
          this.templateFile,
          this.effectiveContexts,
        );
        // Auto-map detected placeholders to known data fields
        this.placeholderMappings = automapPlaceholders(
          this.detectedPlaceholders,
          this.effectiveContexts,
        );
      } catch (error) {
        console.error("Error parsing placeholders:", error);
        this.detectedPlaceholders = null;
        this.placeholderMappings = {};
      } finally {
        this.parsingPlaceholders = false;
      }
    },
    async saveTemplate() {
      if (!this.templateFile) return;

      this.loading = true;

      try {
        const organizationId = this.$route.params.organizationId;
        const versionNumber = 1;

        // 1. Parse placeholders if not already done
        if (!this.detectedPlaceholders) {
          await this.parseTemplateFile();
        }

        // 2. Create the Firestore document first to get the ID
        const docId = await this.$store.dispatch(
          `${MY_ORGANIZATION}${CREATE_DOCUMENT_TEMPLATE}`,
          {
            organizationId,
            name: this.template.name,
            description: this.template.description,
            category: this.template.category,
            placeholderContext: this.template.placeholderContext,
            usageContexts: this.template.usageContexts,
            usagePolicy: this.template.usagePolicy,
            dataMode: this.template.dataMode,
          },
        );

        // 3. Upload the file to versioned storage path
        const storageRef = storage.ref();
        const filePath = `organizations/${organizationId}/documentTemplates/${docId}/v${versionNumber}/${this.templateFile.name}`;
        const uploadTask = storageRef.child(filePath).put(this.templateFile, {
          contentType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        await new Promise((resolve, reject) => {
          uploadTask.on(
            TaskEvent.STATE_CHANGED,
            (snapshot) => {
              this.uploadProgress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            },
            (error) => {
              reject(error);
            },
            async () => {
              // 4. Set activeVersion + versions with filePath
              const { organizationCollection, FieldValue, auth } = await import(
                "@/firebase.js"
              );

              const versionEntry = {
                versionNumber,
                filePath,
                fileName: this.templateFile.name,
                uploadedAt: new Date().toISOString(),
                uploadedBy: auth.currentUser.uid,
                status: "active",
                parsedPlaceholders: this.detectedPlaceholders || {
                  simple: [],
                  loops: [],
                  images: [],
                },
                placeholderMappings: this.placeholderMappings || {},
              };

              await organizationCollection
                .doc(organizationId)
                .collection("documentTemplates")
                .doc(docId)
                .update({
                  activeVersion: versionEntry,
                  versions: [versionEntry],
                  "meta.lastUpdatedAt": FieldValue.serverTimestamp(),
                  "meta.lastUpdatedBy": auth.currentUser.uid,
                });
              resolve();
            },
          );
        });

        // 5. Navigate to the list
        this.$router.push({
          name: "my-organization-document-templates",
          params: { organizationId },
        });
      } catch (error) {
        console.error("Error creating document template:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
